/**
 * Kleda Client App - Custom Hooks
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import type { OrderData, ViewConfig, AnnuleerResponseData } from './types'
import { fetchOrder, kiesAfspraak, wijzigAfspraak, annuleerOrder, reactiveerOrder, updateOpmerkingen, confirmVlekkenbaas, refreshPaymentLink } from './api'
import { STATUS_TO_VIEW, DEFAULT_VIEW_CONFIG } from './types'
import { trackVlekkenbaasViewed, trackVlekkenbaasConfirmed } from './analytics'

// =============================================================================
// useOrder - Main data fetching hook
// =============================================================================

interface UseOrderResult {
  order: OrderData | null
  loading: boolean
  error: string | null
  refetch: () => void
  refetchSilent: () => void
  refetchWithRoutes: () => Promise<void>
  /** Signal that user clicked "Nu betalen" — starts polling on tab return */
  markPaymentInitiated: () => void
  /** True while polling for payment confirmation after Mollie redirect */
  isPaymentPolling: boolean
  /** True when polling exhausted all attempts without finding 'betaald' */
  paymentTimedOut: boolean
  /** Reset timeout state so user can retry the Betalen flow */
  resetPaymentTimedOut: () => void
}

const PAYMENT_POLL_INTERVAL = 3000 // 3 seconds between polls
const PAYMENT_POLL_MAX_ATTEMPTS = 30 // max 90 seconds of polling
const PAYMENT_PENDING_KEY = 'kleda_payment_pending'
const PAYMENT_PENDING_TTL_MS = 10 * 60 * 1000 // 10 minutes

// Payment-pending persistence: localStorage survives page refresh AND works
// cross-tab. Cross-tab is important because Mollie opens in a new tab and
// redirects back to `client.kleda.nl` without the token — the new tab reads
// this entry to recover the token. TTL prevents stale entries.
function readPaymentPending(token: string | null): boolean {
  if (!token || typeof window === 'undefined') return false
  try {
    const raw = localStorage.getItem(PAYMENT_PENDING_KEY)
    if (!raw) return false
    const parsed = JSON.parse(raw) as { token: string; timestamp: number }
    if (parsed.token !== token) return false
    if (Date.now() - parsed.timestamp > PAYMENT_PENDING_TTL_MS) {
      localStorage.removeItem(PAYMENT_PENDING_KEY)
      return false
    }
    return true
  } catch {
    return false
  }
}

/**
 * Recover the most recent pending-payment token if any, without knowing the
 * current token. Used when a customer returns from Mollie to a URL without
 * a token param (Mollie redirects are static).
 */
export function readMostRecentPendingToken(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(PAYMENT_PENDING_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { token: string; timestamp: number }
    if (Date.now() - parsed.timestamp > PAYMENT_PENDING_TTL_MS) {
      localStorage.removeItem(PAYMENT_PENDING_KEY)
      return null
    }
    return parsed.token || null
  } catch {
    return null
  }
}

function writePaymentPending(token: string | null) {
  if (!token || typeof window === 'undefined') return
  try {
    localStorage.setItem(
      PAYMENT_PENDING_KEY,
      JSON.stringify({ token, timestamp: Date.now() })
    )
  } catch {
    // ignore quota / private-mode errors
  }
}

function clearPaymentPending() {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(PAYMENT_PENDING_KEY)
  } catch {
    // ignore
  }
}

export function useOrder(token: string | null): UseOrderResult {
  const [order, setOrder] = useState<OrderData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isPaymentPolling, setIsPaymentPolling] = useState(false)
  const [paymentTimedOut, setPaymentTimedOut] = useState(false)
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isPollingRef = useRef(false)

  const doFetch = useCallback(async (includeExtraRoutes = false, showLoading = true, checkMollie = false) => {
    if (!token) {
      setError('Geen geldige toegangslink')
      setLoading(false)
      return null
    }

    // Alleen loading state tonen bij initiële load, niet bij achtergrond-refresh
    if (showLoading) {
      setLoading(true)
    }
    setError(null)

    try {
      const response = await fetchOrder(token, includeExtraRoutes, checkMollie)
      if (response.success && response.data) {
        setOrder(response.data)
        return response.data
      } else {
        setError(response.error?.message || 'Kon gegevens niet laden')
        return null
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Er ging iets mis bij het ophalen van uw gegevens')
      return null
    } finally {
      if (showLoading) {
        setLoading(false)
      }
    }
  }, [token])

  useEffect(() => {
    doFetch(false, true)
  }, [doFetch])

  // Poll for payment status after user returns from Mollie.
  //
  // BEWUST ZONDER checkMollie (13-08-2026): die vlag liet Apex
  // (KledaClientAppRest.maybeRefreshMolliePaymentStatus → MollieService.
  // processWebhook) zélf een Transactie__c boeken. Sinds de kaart 16-flip is
  // onze eigen Mollie-webhook de autoritatieve schrijver, dus dat maakte de
  // poll een tweede, ongelogde boeker: één betaling in, twee transacties uit
  // (WB-320587 12-08, WB-318689 + WB-321254 13-08). Beide dedups zijn
  // check-then-insert en zagen elkaar binnen dezelfde seconde niet.
  //
  // Zonder de vlag is dit een gewone lees-poll. Dat is snel genoeg: de eigen
  // webhook boekt gemeten 3-5 s na het betaalmoment, en we pollen elke 3 s —
  // de klant ziet 'betaald' bij poging 2 of 3. Blijft de webhook weg, dan
  // vangt de uurlijkse payment-sync-cron (Mollie-first) het af en toont de UI
  // hier de expliciete timeout-status.
  const startPaymentPolling = useCallback(async () => {
    if (isPollingRef.current) return // prevent concurrent polling loops
    isPollingRef.current = true
    setIsPaymentPolling(true)
    setPaymentTimedOut(false)
    let attempts = 0

    const poll = async () => {
      if (attempts >= PAYMENT_POLL_MAX_ATTEMPTS) {
        // Give up — show explicit timeout state, not the "Betalen" button
        setIsPaymentPolling(false)
        setPaymentTimedOut(true)
        clearPaymentPending()
        isPollingRef.current = false
        return
      }
      attempts++
      const data = await doFetch(false, false, false /* checkMollie: zie kop */)
      if (data?.betaalStatus === 'betaald') {
        setIsPaymentPolling(false)
        setPaymentTimedOut(false)
        clearPaymentPending()
        isPollingRef.current = false
      } else {
        pollTimer.current = setTimeout(poll, PAYMENT_POLL_INTERVAL)
      }
    }

    await poll()
  }, [doFetch])

  // On mount: if a previous tab-session left a pending payment flag (refresh
  // while waiting for Mollie), resume polling automatically so the customer
  // doesn't see "Openstaand" while the webhook catches up.
  useEffect(() => {
    if (token && readPaymentPending(token)) {
      startPaymentPolling()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  // Listen for tab visibility change — refetch when user returns from Mollie tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && readPaymentPending(token)) {
        startPaymentPolling()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      if (pollTimer.current) clearTimeout(pollTimer.current)
    }
  }, [startPaymentPolling, token])

  const markPaymentInitiated = useCallback(() => {
    writePaymentPending(token)
    setPaymentTimedOut(false)
  }, [token])

  const resetPaymentTimedOut = useCallback(() => {
    setPaymentTimedOut(false)
  }, [])

  return {
    order,
    loading,
    error,
    refetch: () => { doFetch(false, true) },
    refetchSilent: () => { doFetch(false, false) },
    // Bij extra routes ophalen: geen loading screen tonen (modal blijft open)
    refetchWithRoutes: async () => { await doFetch(true, false) },
    markPaymentInitiated,
    isPaymentPolling,
    paymentTimedOut,
    resetPaymentTimedOut,
  }
}

// =============================================================================
// useToken - Extract token from URL
// =============================================================================

export function useToken(): string | null {
  const [token, setToken] = useState<string | null>(null)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    let tokenParam = params.get('token')

    // Post-Mollie redirect fallback: Mollie's Redirect_URL_Success__c is static
    // and doesn't include the token. If we land here without a token but a
    // recent "payment pending" entry exists, recover the token from there and
    // update the URL so refresh works and the user sees a proper link.
    if (!tokenParam) {
      const recovered = readMostRecentPendingToken()
      if (recovered) {
        tokenParam = recovered
        try {
          const url = new URL(window.location.href)
          url.searchParams.set('token', recovered)
          window.history.replaceState({}, '', url.toString())
        } catch {
          // non-fatal — token still works via state
        }
      }
    }

    setToken(tokenParam)
  }, [])

  return token
}

// =============================================================================
// useView - Determine which view to show based on order status
// =============================================================================

export function useView(order: OrderData | null): ViewConfig {
  if (!order) {
    return { view: 'error', title: 'Laden...' }
  }

  // Check if user already chose an appointment but it's not yet scheduled
  if (order.alGekozen && order.kanAfspraakKiezen) {
    return { view: 'al-gekozen', title: 'Afspraak bevestigd' }
  }

  // Check if user can still choose an appointment
  if (order.kanAfspraakKiezen && !order.alGekozen) {
    return { view: 'kies-afspraak', title: 'Kies ophaalmoment' }
  }

  // Map status to view
  const viewMapping = STATUS_TO_VIEW[order.status]
  if (viewMapping) {
    return viewMapping
  }

  // Fallback
  return DEFAULT_VIEW_CONFIG
}

/**
 * Kan een datumkeuze op deze order het te betalen bedrag raken?
 *
 * Alleen op de aflever-kant: de opslagklok start bij het aflevervoorstel, dus
 * daar verandert een latere datum het bedrag. Op de ophaal-kant staat er niets
 * te herrekenen en is een betaallink-refresh pure wachttijd voor de klant.
 */
function raaktOpslagkosten(order: OrderData | null): boolean {
  return order?.phaseType === 'delivery'
}

// =============================================================================
// useKiesAfspraak - Handle appointment selection
// =============================================================================

interface UseKiesAfspraakResult {
  submit: (routeId: string, opmerkingen?: string) => Promise<void>
  isSubmitting: boolean
  error: string | null
}

export function useKiesAfspraak(
  token: string | null,
  order: OrderData | null,
  onSuccess: () => void
): UseKiesAfspraakResult {
  const [isSubmitting, setIsSubmitting] = useState(false)
  // Dubbele klik binnen milliseconden: de state is dan nog niet bijgewerkt en
  // de knop nog niet uitgeschakeld, en de server kreeg twee verzoeken
  // (WB-325317 21-10: twee ophaalritten). Een ref blokkeert synchroon.
  const inFlight = useRef(false)
  const [error, setError] = useState<string | null>(null)

  const submit = useCallback(async (routeId: string, opmerkingen?: string) => {
    if (!token || !order) {
      setError('Ongeldige sessie')
      return
    }

    if (inFlight.current) return
    inFlight.current = true
    setIsSubmitting(true)
    setError(null)

    try {
      const response = await kiesAfspraak({
        token,
        routeId,
        opmerkingen: opmerkingen || undefined
      })
      if (!response.success) {
        throw new Error(response.error?.message || 'Er ging iets mis bij het bevestigen')
      }
      // Datumkeuze kan server-side kosten aanmaken (opslagkosten bij het
      // zetten van de afleverrit). Betaallink synchroon bijwerken vóór de
      // refetch, net als bij de upsell-flows (zie refreshPaymentLink).
      // Alléén op de aflever-kant: de opslagklok loopt vanaf het
      // aflevervoorstel, dus een ophaaldatum kan het bedrag niet raken. De
      // call kostte gemeten 5,8 s en hield de klant in een blokkerende modal.
      if (raaktOpslagkosten(order)) await refreshPaymentLink(token)
      onSuccess()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Er ging iets mis bij het bevestigen')
      throw err
    } finally {
      inFlight.current = false
      setIsSubmitting(false)
    }
  }, [token, order, onSuccess])

  return {
    submit,
    isSubmitting,
    error
  }
}

// =============================================================================
// useWijzigAfspraak - Handle appointment modification
// =============================================================================

interface UseWijzigAfspraakResult {
  submit: (routeId: string, opmerkingen?: string) => Promise<void>
  isSubmitting: boolean
  error: string | null
}

export function useWijzigAfspraak(
  token: string | null,
  order: OrderData | null,
  onSuccess: () => void
): UseWijzigAfspraakResult {
  const [isSubmitting, setIsSubmitting] = useState(false)
  // Dubbele klik binnen milliseconden: de state is dan nog niet bijgewerkt en
  // de knop nog niet uitgeschakeld, en de server kreeg twee verzoeken
  // (WB-325317 21-10: twee ophaalritten). Een ref blokkeert synchroon.
  const inFlight = useRef(false)
  const [error, setError] = useState<string | null>(null)

  const submit = useCallback(async (routeId: string, opmerkingen?: string) => {
    if (!token) {
      setError('Ongeldige sessie')
      return
    }

    if (inFlight.current) return
    inFlight.current = true
    setIsSubmitting(true)
    setError(null)

    try {
      const response = await wijzigAfspraak({
        token,
        routeId,
        opmerkingen
      })
      if (!response.success) {
        throw new Error(response.error?.message || 'Wijzigen mislukt')
      }
      // Zelfde reden als bij kiesAfspraak: een gewijzigde afleverrit kan de
      // opslagkosten herberekenen; link bijwerken vóór de refetch. En om
      // dezelfde reden op de ophaal-kant niet: daar valt geen bedrag te
      // herrekenen.
      if (raaktOpslagkosten(order)) await refreshPaymentLink(token)
      onSuccess()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Er ging iets mis bij het wijzigen'
      setError(message)
      throw err
    } finally {
      inFlight.current = false
      setIsSubmitting(false)
    }
  }, [token, order, onSuccess])

  return {
    submit,
    isSubmitting,
    error
  }
}

// =============================================================================
// useAnnuleer - Handle order cancellation
// =============================================================================

interface UseAnnuleerResult {
  submit: (reden?: string) => Promise<AnnuleerResponseData | null>
  isSubmitting: boolean
  error: string | null
}

export function useAnnuleer(
  token: string | null,
  onSuccess: () => void
): UseAnnuleerResult {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = useCallback(async (reden?: string): Promise<AnnuleerResponseData | null> => {
    if (!token) {
      setError('Ongeldige sessie')
      return null
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const response = await annuleerOrder({
        token,
        reden
      })
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Annuleren mislukt')
      }
      // Bij gratis annulering meteen refetchen; bij fee laat de modal eerst
      // de payment-CTA zien en wordt onSuccess later via onComplete getriggerd.
      if (!response.data.feePaymentUrl) {
        onSuccess()
      }
      return response.data
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Er ging iets mis bij het annuleren'
      setError(message)
      throw err
    } finally {
      setIsSubmitting(false)
    }
  }, [token, onSuccess])

  return {
    submit,
    isSubmitting,
    error
  }
}

// =============================================================================
// useReactiveer - Heropen een geannuleerde order ("toch laten reinigen")
// =============================================================================

interface UseReactiveerResult {
  submit: () => Promise<void>
  isSubmitting: boolean
  error: string | null
}

export function useReactiveer(
  token: string | null,
  onSuccess: () => void
): UseReactiveerResult {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = useCallback(async () => {
    if (!token) {
      setError('Ongeldige sessie')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const response = await reactiveerOrder({ token })
      if (!response.success) {
        throw new Error(response.error?.message || 'Heropenen mislukt')
      }
      // Status staat nu op 'Ophalen plannen' — refetch laat de klant direct
      // een nieuw ophaalmoment kiezen.
      onSuccess()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Er ging iets mis bij het heropenen'
      setError(message)
      throw err
    } finally {
      setIsSubmitting(false)
    }
  }, [token, onSuccess])

  return {
    submit,
    isSubmitting,
    error
  }
}

// =============================================================================
// useOpmerkingen - Handle adding/updating remarks
// =============================================================================

interface UseOpmerkingenResult {
  submit: (opmerkingen: string, verwacht: string | null) => Promise<void>
  isSubmitting: boolean
  error: string | null
  success: boolean
}

export function useOpmerkingen(
  token: string | null,
  phaseType: 'pickup' | 'delivery' | undefined,
  onSuccess: () => void
): UseOpmerkingenResult {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const submit = useCallback(async (opmerkingen: string, verwacht: string | null) => {
    if (!token) {
      setError('Ongeldige sessie')
      throw new Error('Ongeldige sessie')
    }

    setIsSubmitting(true)
    setError(null)
    setSuccess(false)

    try {
      const response = await updateOpmerkingen({
        token,
        opmerkingenOphalen: phaseType === 'pickup' ? opmerkingen : undefined,
        opmerkingenAfleveren: phaseType === 'delivery' ? opmerkingen : undefined,
        verwachtOphalen: phaseType === 'pickup' ? verwacht : undefined,
        verwachtAfleveren: phaseType === 'delivery' ? verwacht : undefined
      })
      if (!response.success) {
        throw new Error(response.error?.message || 'Opslaan mislukt')
      }
      setSuccess(true)
      onSuccess()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Er ging iets mis bij het opslaan'
      setError(message)
      throw err
    } finally {
      setIsSubmitting(false)
    }
  }, [token, phaseType, onSuccess])

  return {
    submit,
    isSubmitting,
    error,
    success
  }
}

// =============================================================================
// useHighlight - Extract highlight parameter from URL for scroll/animation
// =============================================================================

/**
 * Extracts the 'highlight' URL parameter used in email CTAs to scroll
 * and animate specific sections (e.g., ?highlight=ondertapijt).
 * Returns null if no highlight parameter is present.
 */
export type HighlightSection = 'rand-reparatie' | 'ondertapijt' | 'vlekkenbaas' | null

export function useHighlight(): HighlightSection {
  const [highlight, setHighlight] = useState<HighlightSection>(null)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const highlightParam = params.get('highlight')

    if (highlightParam === 'rand-reparatie' || highlightParam === 'ondertapijt' || highlightParam === 'vlekkenbaas') {
      setHighlight(highlightParam)
    }
  }, [])

  return highlight
}

// =============================================================================
// usePageType - Extract page type parameter from URL
// =============================================================================

/**
 * Extracts the 'type' or 'view' URL parameter to route to specific standalone pages.
 * - ?type=ondertapijt  → focused ondertapijt upsell page
 * - ?view=voorinspectie → voorinspectie report (always shown when data available)
 * - ?view=fotos → foto- en video-timeline van de kleden
 */
export type PageType = 'ondertapijt' | 'voorinspectie' | 'fotos' | 'terugkom' | 'mijn-kleda' | null

export function usePageType(): PageType {
  const [pageType, setPageType] = useState<PageType>(null)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const typeParam = params.get('type')
    const viewParam = params.get('view')

    if (typeParam === 'ondertapijt' || typeParam === 'upsell') {
      setPageType('ondertapijt')
    } else if (typeParam === 'terugkom') {
      setPageType('terugkom')
    } else if (typeParam === 'mijn-kleda') {
      setPageType('mijn-kleda')
    } else if (viewParam === 'voorinspectie') {
      setPageType('voorinspectie')
    } else if (viewParam === 'fotos') {
      setPageType('fotos')
    }
  }, [])

  return pageType
}

// =============================================================================
// useVoorkeuze - keuze uit een mail-link (?abonnement=1&interval=12&bron=…)
// =============================================================================

const VOLGENDE_KEER_BRONNEN = ['client-app', 'aflever-fase', 'mail-link', 's10-chip', 's10-whatsapp', 'care-guide', 'mijn-kleda'] as const
const RITMES = [6, 12, 18, 24]

/**
 * De S10-chips, de care-guide en WhatsApp openen ?type=terugkom met het ritme
 * van het kleed-abonnement voorgekozen (plan 2026-10-26; sinds 27-10 alleen
 * het abonnement). Oudere mails (tot 27-10) gaven ?kies=<seizoen> mee voor
 * een losse herinnering; de kaart leest dat als startseizoen. Een klik in de
 * mail legt niets vast; de kaart toont de keuze en de klant bevestigt.
 */
export function useVoorkeuze(): { seizoen: string | null; abonnement: boolean; interval: number | null; bron: typeof VOLGENDE_KEER_BRONNEN[number] } {
  const params = new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search)
  const bron = params.get('bron')
  const interval = Number(params.get('interval'))
  return {
    seizoen: params.get('kies'),
    abonnement: params.get('abonnement') === '1',
    interval: RITMES.includes(interval) ? interval : null,
    bron: bron && (VOLGENDE_KEER_BRONNEN as readonly string[]).includes(bron) ? (bron as typeof VOLGENDE_KEER_BRONNEN[number]) : 'mail-link',
  }
}

// =============================================================================
// useVlekkenbaas - Handle vlekkenbaas upsell
// =============================================================================

interface UseVlekkenbaasResult {
  isEligible: boolean
  isOrdered: boolean
  orderedQuantity: number
  unitPrice: number
  quantity: number
  totalPrice: number
  setQuantity: (q: number) => void
  submit: () => Promise<void>
  isSubmitting: boolean
  error: string | null
  success: boolean
}

const VLEKKENBAAS_MAX_QUANTITY = 5

export function useVlekkenbaas(
  token: string | null,
  order: OrderData | null,
  onSuccess: () => void
): UseVlekkenbaasResult {
  const [quantity, setQuantityRaw] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [hasTrackedView, setHasTrackedView] = useState(false)

  const vlekkenbaas = order?.vlekkenbaas
  const isEligible = vlekkenbaas?.eligible ?? false
  const isOrdered = vlekkenbaas?.alreadyOrdered ?? false
  const orderedQuantity = vlekkenbaas?.orderedQuantity ?? 0
  const unitPrice = vlekkenbaas?.priceInclVat ?? 17.50
  const totalPrice = unitPrice * quantity

  const setQuantity = useCallback((q: number) => {
    setQuantityRaw(Math.max(1, Math.min(VLEKKENBAAS_MAX_QUANTITY, q)))
  }, [])

  // Track view once when eligible and not yet ordered
  useEffect(() => {
    if (!hasTrackedView && isEligible && !isOrdered && order) {
      trackVlekkenbaasViewed(order.werkOrderId, unitPrice)
      setHasTrackedView(true)
    }
  }, [hasTrackedView, isEligible, isOrdered, order, unitPrice])

  const submit = useCallback(async () => {
    if (!token) {
      setError('Ongeldige sessie')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const response = await confirmVlekkenbaas({ token, confirm: true, quantity })
      if (response.success) {
        setSuccess(true)
        if (order) {
          trackVlekkenbaasConfirmed(order.werkOrderId, totalPrice)
        }
        // Regenereer de betaallink synchroon vóór de refetch, zodat het nieuwe
        // bedrag (incl. vlekkenbaas) in de link staat voordat de klant betaalt.
        // Best-effort: bij falen vangt het async trigger-vangnet het later op.
        await refreshPaymentLink(token)
        onSuccess()
      } else {
        setError(response.message || 'Er ging iets mis')
      }
    } catch {
      setError('Er ging iets mis. Probeer het later opnieuw.')
    } finally {
      setIsSubmitting(false)
    }
  }, [token, order, quantity, totalPrice, onSuccess])

  return {
    isEligible,
    isOrdered,
    orderedQuantity,
    unitPrice,
    quantity,
    totalPrice,
    setQuantity,
    submit,
    isSubmitting,
    error,
    success
  }
}
